import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { 
    TruckIcon, 
    CheckCircleIcon, 
    ClockIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';

const ShippingTestPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [shipments, setShipments] = useState([]);
    const [testResults, setTestResults] = useState({});

    useEffect(() => {
        if (user) {
            fetchTestData();
        }
    }, [user]);

    const fetchTestData = async () => {
        try {
            setLoading(true);
            
            // Siparişleri al
            const ordersResponse = await api.get('/api/orders');
            const completedOrders = ordersResponse.data.filter(order => 
                order.status === 'completed' || order.status === 'pending'
            ).slice(0, 5);
            setOrders(completedOrders);

            // Kargoları al
            const shipmentsResponse = await api.get('/api/shipping/list');
            setShipments(shipmentsResponse.data || []);

        } catch (error) {
            console.error('Test verileri alınırken hata:', error);
            toast.error('Test verileri yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const testKargonomiConnection = async () => {
        try {
            setLoading(true);
            setTestResults(prev => ({ ...prev, connection: 'testing' }));

            const response = await api.get('/api/shipping/cities');
            
            setTestResults(prev => ({ 
                ...prev, 
                connection: 'success',
                citiesCount: response.data?.data?.length || 0
            }));
            
            toast.success('Kargonomi API bağlantısı başarılı!');
        } catch (error) {
            setTestResults(prev => ({ ...prev, connection: 'error' }));
            toast.error('Kargonomi API bağlantısı başarısız: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const testCreateShipment = async (orderId) => {
        try {
            setLoading(true);
            setTestResults(prev => ({ ...prev, [`shipment_${orderId}`]: 'testing' }));

            const response = await api.post(`/api/shipping/create/${orderId}`);
            
            setTestResults(prev => ({ 
                ...prev, 
                [`shipment_${orderId}`]: 'success'
            }));
            
            toast.success('Kargo başarıyla oluşturuldu!');
            fetchTestData(); // Verileri yenile
        } catch (error) {
            setTestResults(prev => ({ ...prev, [`shipment_${orderId}`]: 'error' }));
            toast.error('Kargo oluşturulamadı: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'testing':
                return <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />;
            case 'success':
                return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
            case 'error':
                return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
            default:
                return <ClockIcon className="h-5 w-5 text-gray-400" />;
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Giriş Gerekli</h2>
                    <p className="text-gray-600">Bu sayfayı görüntülemek için giriş yapmalısınız.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                        <TruckIcon className="h-8 w-8 mr-3 text-blue-600" />
                        Kargo Sistemi Test Paneli
                    </h1>
                    <p className="mt-2 text-gray-600">
                        Kargonomi API entegrasyonu ve kargo yönetim sistemini test edin.
                    </p>
                </div>

                {/* API Bağlantı Testi */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">1. API Bağlantı Testi</h2>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                {getStatusIcon(testResults.connection)}
                                <span className="text-gray-700">
                                    Kargonomi API Bağlantısı
                                    {testResults.citiesCount && (
                                        <span className="text-sm text-gray-500 ml-2">
                                            ({testResults.citiesCount} şehir)
                                        </span>
                                    )}
                                </span>
                            </div>
                            <button
                                onClick={testKargonomiConnection}
                                disabled={loading}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                            >
                                Bağlantıyı Test Et
                            </button>
                        </div>
                    </div>
                </div>

                {/* Sipariş Listesi */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">2. Test Siparişleri</h2>
                        {loading ? (
                            <div className="text-center py-4">
                                <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin mx-auto" />
                                <p className="mt-2 text-gray-600">Yükleniyor...</p>
                            </div>
                        ) : orders.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">Test edilecek sipariş bulunamadı.</p>
                        ) : (
                            <div className="space-y-4">
                                {orders.map(order => (
                                    <div key={order.id} className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="font-medium">{order.order_number}</h3>
                                                <p className="text-sm text-gray-600">
                                                    {order.shipping_name || order.guest_name} - ₺{order.total_price}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    Durum: {order.status}
                                                </p>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                {getStatusIcon(testResults[`shipment_${order.id}`])}
                                                <button
                                                    onClick={() => testCreateShipment(order.id)}
                                                    disabled={loading}
                                                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
                                                >
                                                    Kargo Oluştur
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Mevcut Kargolar */}
                <div className="bg-white rounded-lg shadow">
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">3. Mevcut Kargolar</h2>
                        {shipments.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">Henüz kargo kaydı bulunmuyor.</p>
                        ) : (
                            <div className="space-y-4">
                                {shipments.map(shipment => (
                                    <div key={shipment.id} className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="font-medium">
                                                    Kargo #{shipment.id}
                                                </h3>
                                                <p className="text-sm text-gray-600">
                                                    Sipariş: {shipment.orders?.order_number}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    Durum: {shipment.status} | 
                                                    Takip: {shipment.tracking_code || 'Henüz yok'}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-medium">
                                                    {shipment.shipping_provider || 'Kargo firması seçilmedi'}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    ₺{shipment.shipping_cost || '0'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Test Sonuçları */}
                {Object.keys(testResults).length > 0 && (
                    <div className="mt-6 bg-gray-100 rounded-lg p-4">
                        <h3 className="font-medium mb-2">Test Sonuçları:</h3>
                        <pre className="text-xs text-gray-600 overflow-auto">
                            {JSON.stringify(testResults, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ShippingTestPage;